import React, { useState, useEffect } from 'react';
import api, { voiceAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { VoiceAgentTester } from '../../components/voice/VoiceAgentTester';
import { toast } from 'sonner';
import { 
  PhoneForwarded, 
  ShieldAlert, 
  Settings, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Phone,
  UserCircle,
  Bot,
  Lock,
  Key,
  ExternalLink,
  Trash2,
  TestTube,
  Loader2,
  Info,
  Eye,
  Globe,
  MapPin,
  Search,
  Languages,
  Zap,
  Database,
  Sparkles,
  ShieldCheck
} from 'lucide-react';

/**
 * Dedicated Phone Number provisioning (platform-managed caller ID).
 * Admin searches by country/area code, buys a number, views/releases it.
 */
function DedicatedNumberCard() {
  const [owned, setOwned] = useState([]);
  const [activeNumber, setActiveNumber] = useState(null);
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState('');

  useEffect(() => { loadNumbers(); }, []);

  const loadNumbers = async () => {
    try {
      const res = await voiceAPI.listNumbers();
      setOwned(res.data.numbers || []);
      setActiveNumber(res.data.active_number || null);
    } catch (e) {
      console.error('Failed to load numbers', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    setResults([]);
    try {
      const res = await voiceAPI.searchNumbers(country.toUpperCase(), areaCode.trim());
      setResults(res.data.numbers || []);
      if (!(res.data.numbers || []).length) toast.info('No numbers found. Try a different area code.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleBuy = async (phone) => {
    if (!window.confirm(`Buy ${phone}? A monthly rental will be charged to your credits.`)) return;
    setBuying(phone);
    try {
      await voiceAPI.purchaseNumber(phone);
      toast.success(`${phone} is now your dedicated caller ID`);
      setResults([]);
      await loadNumbers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purchase failed');
    } finally {
      setBuying('');
    }
  };

  const handleRelease = async (sid, phone) => {
    if (!window.confirm(`Release ${phone}? This stops the monthly rental and removes the number.`)) return;
    try {
      await voiceAPI.releaseNumber(sid);
      toast.success(`${phone} released`);
      await loadNumbers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release failed');
    }
  };

  const activeRec = owned.find((n) => n.status === 'active');

  return (
    <Card data-testid="dedicated-number-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" /> Dedicated Phone Number
        </CardTitle>
        <CardDescription>
          Get your own number as the outbound caller ID. Avoids the shared-number spam-flag risk
          and builds trust with leads. Monthly rental is billed to your credits.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Skeleton className="h-12 w-full" />
        ) : activeRec ? (
          <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30" data-testid="active-number-display">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-semibold">{activeRec.phone_number}</p>
                <p className="text-xs text-muted-foreground">Active caller ID · managed</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-red-600"
              data-testid="number-release-btn"
              onClick={() => handleRelease(activeRec.twilio_sid, activeRec.phone_number)}>
              <Trash2 className="h-4 w-4 mr-1" /> Release
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Country</Label>
                <Input className="w-20 uppercase" maxLength={2} value={country}
                  data-testid="number-country-input"
                  onChange={(e) => setCountry(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Area code (optional)</Label>
                <Input className="w-32" placeholder="e.g. 415" value={areaCode}
                  data-testid="number-areacode-input"
                  onChange={(e) => setAreaCode(e.target.value)} />
              </div>
              <Button onClick={handleSearch} disabled={searching} data-testid="number-search-btn">
                {searching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Search
              </Button>
            </div>

            {results.length > 0 && (
              <div className="space-y-2" data-testid="number-results">
                {results.map((n) => (
                  <div key={n.phone_number}
                    className="flex items-center justify-between rounded-lg border p-3"
                    data-testid={`number-result-${n.phone_number}`}>
                    <div>
                      <p className="font-medium">{n.friendly_name || n.phone_number}</p>
                      <p className="text-xs text-muted-foreground">{[n.locality, n.region].filter(Boolean).join(', ') || n.iso_country}</p>
                    </div>
                    <Button size="sm" disabled={buying === n.phone_number}
                      data-testid={`number-buy-btn-${n.phone_number}`}
                      onClick={() => handleBuy(n.phone_number)}>
                      {buying === n.phone_number ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                      Buy
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Voice AI Settings Page
 * 
 * Tab 1: Human Transfer Settings
 * - Enable/disable Human Transfer for tenant
 * - Configure fallback agent number
 * 
 * Tab 2: BYOK (Bring Your Own Key)
 * - Configure Twilio credentials for voice
 * - Configure ElevenLabs credentials for TTS
 * 
 * Governance enforced:
 * - Demo tenant cannot modify settings
 * - Only admins can update settings
 */

function MarketLanguageTab() {
  const [accents, setAccents] = useState([]);
  const [testLeadId, setTestLeadId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    loadAccents();
    loadLeads();
  }, []);

  const loadAccents = async () => {
    try {
      const res = await api.get('/api/voice/market/accent-voices');
      setAccents(res.data.accents || []);
    } catch (e) {
      console.error('Failed to load accents', e);
    }
  };

  const loadLeads = async () => {
    try {
      const res = await api.get('/api/leads/?limit=10');
      const list = Array.isArray(res.data) ? res.data : (res.data.leads || []);
      setLeads(list);
      if (list.length > 0 && !testLeadId) setTestLeadId(list[0].id);
    } catch (e) {
      console.error('Failed to load leads', e);
    }
  };

  const testMarketDetection = async () => {
    if (!testLeadId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.get(`/api/voice/market/detect/${testLeadId}`);
      setTestResult(res.data);
    } catch (e) {
      toast.error('Detection failed');
    } finally {
      setTesting(false);
    }
  };

  const accentLabel = {
    us_english: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', flag: 'US' },
    indian_english: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300', flag: 'IN' },
    hinglish: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300', flag: 'HI' },
  };

  return (
    <>
      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="market-language-title">
            <Globe className="h-5 w-5" />
            Market-Aware Voice Routing
          </CardTitle>
          <CardDescription>
            The Brain automatically detects each lead's market and selects the right accent, language, and cultural tone.
            Indian leads hear Indian English. US leads hear American English. No manual configuration needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {accents.map(a => {
              const style = accentLabel[a.key] || { color: 'bg-gray-100 text-gray-700', flag: '??' };
              return (
                <div key={a.key} className="rounded-lg border p-4 space-y-2" data-testid={`accent-card-${a.key}`}>
                  <div className="flex items-center gap-2">
                    <Badge className={style.color}>{style.flag}</Badge>
                    <span className="font-medium text-sm">{a.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Model: {a.model === 'eleven_multilingual_v2' ? 'Multilingual v2' : 'Monolingual v1'}
                  </p>
                </div>
              );
            })}
          </div>

          <Separator />

          {/* Detection Waterfall */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Detection Waterfall (8-step)
            </h4>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>1. Lead's country field &rarr; 2. Phone country code (+91=India) &rarr; 3. Raw phone parse &rarr; 4. Email TLD (.co.in=India)</p>
              <p>5. Timezone &rarr; 6. Campaign target market &rarr; 7. Tenant primary market &rarr; 8. Default: US English</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Detection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            Test Market Detection
          </CardTitle>
          <CardDescription>
            Select a lead to see how the Brain would detect their market, accent, and language.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs">Select Lead</Label>
              <select
                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={testLeadId}
                onChange={(e) => setTestLeadId(e.target.value)}
                data-testid="market-test-lead-select"
              >
                {leads.map(l => (
                  <option key={l.id} value={l.id}>{l.name} — {l.company || 'N/A'}</option>
                ))}
              </select>
            </div>
            <Button onClick={testMarketDetection} disabled={testing || !testLeadId} data-testid="market-test-btn">
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
              Detect
            </Button>
          </div>

          {testResult && (
            <div className="rounded-lg border p-4 space-y-3 bg-muted/30" data-testid="market-test-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Market</p>
                  <p className="font-medium text-sm">{testResult.market_label} ({testResult.market_code})</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Accent</p>
                  <Badge className={(accentLabel[testResult.accent] || {}).color || 'bg-gray-100'}>
                    {testResult.accent?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Language</p>
                  <p className="font-medium text-sm">{testResult.language === 'hinglish' ? 'Hindi/Hinglish' : testResult.language === 'en' ? 'English' : testResult.language}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Voice Model</p>
                  <p className="font-medium text-sm">{testResult.voice_model === 'eleven_multilingual_v2' ? 'Multilingual v2' : 'Monolingual v1'}</p>
                </div>
              </div>
              {testResult.india_region && (
                <div>
                  <p className="text-xs text-muted-foreground">India Region</p>
                  <Badge variant="outline">{testResult.india_region === 'south' ? 'South India (English only)' : 'North India'}</Badge>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Detection Chain</p>
                <p className="text-sm">{testResult.detection_chain?.join(' → ')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Cultural Rules</p>
                <p className="text-xs italic">{testResult.cultural_rules}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function VoiceQualityTab() {
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);
  const [accent, setAccent] = useState('us_english');
  const [nativeRag, setNativeRag] = useState(false);
  const [syncedDocs, setSyncedDocs] = useState(0);
  const [ragLoading, setRagLoading] = useState(true);
  const [ragSaving, setRagSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { loadRag(); }, []);

  const loadRag = async () => {
    setRagLoading(true);
    try {
      const res = await voiceAPI.getNativeRagSettings();
      setNativeRag(!!res.data?.data?.enabled);
      setSyncedDocs(res.data?.data?.synced_docs || 0);
    } catch (e) {
      console.error('Failed to load native RAG settings', e);
    } finally {
      setRagLoading(false);
    }
  };

  const runTestProvision = async () => {
    setProvisioning(true);
    setProvisionResult(null);
    try {
      const res = await voiceAPI.testProvisionAgent({ accent, call_type: 'discovery', provider: 'twilio' });
      setProvisionResult(res.data?.data || null);
      if (res.data?.success) toast.success('Agent provisioned & verified — no call was placed');
      else toast.error(res.data?.message || 'Provisioning failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Provisioning failed');
    } finally {
      setProvisioning(false);
    }
  };

  const toggleNativeRag = async (checked) => {
    setRagSaving(true);
    try {
      const res = await voiceAPI.updateNativeRagSettings(checked);
      setNativeRag(checked);
      setSyncedDocs(res.data?.data?.synced_docs || 0);
      toast.success(`Native RAG ${checked ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update native RAG');
    } finally {
      setRagSaving(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await voiceAPI.syncNativeRag();
      const d = res.data?.data || {};
      setSyncedDocs((prev) => (d.synced || 0) + (d.skipped || 0) || prev);
      toast.success(`Synced ${d.synced || 0}, skipped ${d.skipped || 0}, failed ${d.failed || 0}`);
      loadRag();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const CheckRow = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm" data-testid={`config-check-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {ok ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
      <span className={ok ? '' : 'text-muted-foreground'}>{label}</span>
    </div>
  );

  return (
    <>
      {/* 3D — Voice quality config + Test provision */}
      <Card data-testid="voice-quality-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" /> Voice Quality Engine (v12)
          </CardTitle>
          <CardDescription>
            Engine-level turn-taking, highest-quality (Scribe) speech recognition, automatic
            language switching, and a spoken filler before knowledge lookups (no dead air).
            Verify the live agent provisions correctly before it touches production calls — no call is placed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <CheckRow ok label="Turn-taking v2 (timeout + eagerness)" />
            <CheckRow ok label="Scribe high-quality STT" />
            <CheckRow ok label="Auto language detection (EN ↔ HI)" />
            <CheckRow ok label="Pre-tool speech on KB lookup" />
          </div>
          <Separator />
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Accent to verify</Label>
              <select
                className="w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                data-testid="test-provision-accent-select"
              >
                <option value="us_english">American English</option>
                <option value="indian_english">Indian English</option>
                <option value="hinglish">Hindi / Hinglish</option>
              </select>
            </div>
            <Button onClick={runTestProvision} disabled={provisioning} data-testid="test-provision-btn">
              {provisioning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
              Verify Agent Config
            </Button>
          </div>

          {provisionResult && (
            <div className="rounded-lg border p-4 space-y-3 bg-muted/30" data-testid="test-provision-result">
              <div className="flex items-center gap-2">
                {provisionResult.all_passed
                  ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"><ShieldCheck className="h-3 w-3 mr-1" /> All checks passed</Badge>
                  : <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" /> Partial</Badge>}
                <span className="text-xs text-muted-foreground font-mono">agent: {provisionResult.agent_id}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <CheckRow ok={provisionResult.config_checks?.turn_taking} label="Turn taking" />
                <CheckRow ok={provisionResult.config_checks?.asr_quality_high} label="ASR quality high" />
                <CheckRow ok={provisionResult.config_checks?.language_detection} label="Language detection" />
                <CheckRow ok={provisionResult.config_checks?.pre_tool_speech} label="Pre tool speech" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3C — Per-tenant native RAG */}
      <Card data-testid="native-rag-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" /> Native Knowledge Retrieval (RAG)
          </CardTitle>
          <CardDescription>
            Graduate to a dedicated per-tenant voice agent with native RAG —
            ~155ms grounded answers with native source attribution, instead of the slower
            live webhook lookup. Your knowledge base stays isolated to your own agent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {ragLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            <>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium">Enable Native RAG</Label>
                    {nativeRag
                      ? <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"><Sparkles className="h-3 w-3 mr-1" /> Graduated</Badge>
                      : <Badge variant="secondary">Shared agent</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {syncedDocs} document{syncedDocs === 1 ? '' : 's'} synced to your voice engine
                  </p>
                </div>
                <Switch
                  checked={nativeRag}
                  onCheckedChange={toggleNativeRag}
                  disabled={ragSaving}
                  data-testid="native-rag-toggle"
                />
              </div>
              {nativeRag && (
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={runSync} disabled={syncing} data-testid="native-rag-sync-btn">
                    {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Sync KB to Voice
                  </Button>
                  <p className="text-xs text-muted-foreground">One click after adding or editing KB documents — pushes them to your voice agent.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default function VoiceAISettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('persona');
  
  // Transfer settings state
  const [settings, setSettings] = useState({
    human_transfer_enabled: false,
    fallback_agent_number: '',
    is_demo: false
  });
  const [originalSettings, setOriginalSettings] = useState(null);

  // Voice Persona state
  const [persona, setPersona] = useState(null);
  const [personaOverrides, setPersonaOverrides] = useState({
    agent_name: '', tone: '', custom_instructions: '', custom_objections: ''
  });
  const [savingPersona, setSavingPersona] = useState(false);
  const [loadingPersona, setLoadingPersona] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [promptPreview, setPromptPreview] = useState('');
  
  // BYOK state
  const [integrations, setIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [twilioConfig, setTwilioConfig] = useState({
    account_sid: '',
    api_key: '',
    api_secret: '',
    is_enabled: false
  });
  const [elevenlabsConfig, setElevenlabsConfig] = useState({
    api_key: '',
    is_enabled: false
  });
  const [savingByok, setSavingByok] = useState(null); // 'twilio' | 'elevenlabs' | null
  const [testingByok, setTestingByok] = useState(null);

  useEffect(() => {
    loadSettings();
    loadIntegrations();
    loadPersona();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await voiceAPI.getTransferSettings();
      const data = response.data;
      setSettings({
        human_transfer_enabled: data.human_transfer_enabled || false,
        fallback_agent_number: data.fallback_agent_number || '',
        is_demo: data.is_demo || false
      });
      setOriginalSettings({
        human_transfer_enabled: data.human_transfer_enabled || false,
        fallback_agent_number: data.fallback_agent_number || ''
      });
    } catch (error) {
      console.error('Failed to load transfer settings:', error);
      toast.error('Failed to load Voice AI settings');
    } finally {
      setLoading(false);
    }
  };
  
  const loadIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const response = await api.get('/api/tenant/integrations');
      setIntegrations(response.data);
      
      // Find existing configs
      const twilio = response.data.find(i => i.provider === 'twilio');
      const elevenlabs = response.data.find(i => i.provider === 'elevenlabs');
      
      if (twilio?.is_configured) {
        setTwilioConfig(prev => ({
          ...prev,
          is_enabled: twilio.is_enabled,
          // Don't show the actual keys, just indicate configured
          _configured: true,
          _key_prefix: twilio.key_prefix,
          _account_sid_prefix: twilio.account_sid_prefix
        }));
      }
      
      if (elevenlabs?.is_configured) {
        setElevenlabsConfig(prev => ({
          ...prev,
          is_enabled: elevenlabs.is_enabled,
          _configured: true,
          _key_prefix: elevenlabs.key_prefix
        }));
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const loadPersona = async () => {
    setLoadingPersona(true);
    try {
      const [personaRes, overridesRes] = await Promise.all([
        voiceAPI.getPersona(),
        voiceAPI.getPersonaOverrides(),
      ]);
      setPersona(personaRes.data);
      setPersonaOverrides({
        agent_name: overridesRes.data?.agent_name || '',
        tone: overridesRes.data?.tone || '',
        custom_instructions: overridesRes.data?.custom_instructions || '',
        custom_objections: overridesRes.data?.custom_objections || '',
      });
    } catch (error) {
      console.error('Failed to load persona:', error);
    } finally {
      setLoadingPersona(false);
    }
  };

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      const res = await voiceAPI.updatePersonaOverrides(personaOverrides);
      setPersona(res.data);
      toast.success('Voice persona updated!');
    } catch (error) {
      toast.error('Failed to update persona');
    } finally {
      setSavingPersona(false);
    }
  };

  const loadPromptPreview = async () => {
    try {
      const res = await voiceAPI.previewPersonaPrompt();
      setPromptPreview(res.data?.system_prompt_preview || '');
      setShowPromptPreview(true);
    } catch (error) {
      toast.error('Failed to load prompt preview');
    }
  };

  const handleSave = async () => {
    if (!settings.fallback_agent_number && settings.human_transfer_enabled) {
      toast.error('Please enter a fallback agent number before enabling Human Transfer');
      return;
    }

    setSaving(true);
    try {
      await voiceAPI.updateTransferSettings(
        settings.human_transfer_enabled,
        settings.fallback_agent_number
      );
      toast.success('Voice AI settings saved successfully');
      setOriginalSettings({
        human_transfer_enabled: settings.human_transfer_enabled,
        fallback_agent_number: settings.fallback_agent_number
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const saveTwilioConfig = async () => {
    if (twilioConfig.is_enabled && (!twilioConfig.account_sid || !twilioConfig.api_key || !twilioConfig.api_secret)) {
      toast.error('Please fill in all Twilio credentials');
      return;
    }
    
    setSavingByok('twilio');
    try {
      await api.post('/api/tenant/integrations/twilio', {
        provider: 'twilio',
        account_sid: twilioConfig.account_sid || undefined,
        api_key: twilioConfig.api_key || undefined,
        api_secret: twilioConfig.api_secret || undefined,
        is_enabled: twilioConfig.is_enabled
      });
      toast.success('Twilio integration saved');
      loadIntegrations();
      // Clear sensitive fields
      setTwilioConfig(prev => ({
        ...prev,
        account_sid: '',
        api_key: '',
        api_secret: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Twilio configuration');
    } finally {
      setSavingByok(null);
    }
  };
  
  const saveElevenlabsConfig = async () => {
    if (elevenlabsConfig.is_enabled && !elevenlabsConfig.api_key) {
      toast.error('Please enter your ElevenLabs API key');
      return;
    }
    
    setSavingByok('elevenlabs');
    try {
      await api.post('/api/tenant/integrations/elevenlabs', {
        provider: 'elevenlabs',
        api_key: elevenlabsConfig.api_key || undefined,
        is_enabled: elevenlabsConfig.is_enabled
      });
      toast.success('ElevenLabs integration saved');
      loadIntegrations();
      // Clear sensitive field
      setElevenlabsConfig(prev => ({
        ...prev,
        api_key: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ElevenLabs configuration');
    } finally {
      setSavingByok(null);
    }
  };
  
  const testIntegration = async (provider) => {
    setTestingByok(provider);
    try {
      const response = await api.post(`/api/tenant/integrations/${provider}/test`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTestingByok(null);
    }
  };
  
  const removeIntegration = async (provider) => {
    if (!confirm(`Are you sure you want to remove the ${provider} integration? You'll revert to using platform credits.`)) {
      return;
    }
    
    try {
      await api.delete(`/api/tenant/integrations/${provider}`);
      toast.success(`${provider} integration removed`);
      loadIntegrations();
      
      if (provider === 'twilio') {
        setTwilioConfig({
          account_sid: '',
          api_key: '',
          api_secret: '',
          is_enabled: false
        });
      } else if (provider === 'elevenlabs') {
        setElevenlabsConfig({
          api_key: '',
          is_enabled: false
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove integration');
    }
  };

  const hasChanges = originalSettings && (
    originalSettings.human_transfer_enabled !== settings.human_transfer_enabled ||
    originalSettings.fallback_agent_number !== settings.fallback_agent_number
  );
  
  const twilioIntegration = integrations.find(i => i.provider === 'twilio');
  const elevenlabsIntegration = integrations.find(i => i.provider === 'elevenlabs');

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="voice-ai-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Bot className="h-8 w-8 text-primary" />
            Voice AI Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure voice AI, call transfers, and integration credentials
          </p>
        </div>
        <Button variant="outline" onClick={() => { loadSettings(); loadIntegrations(); }} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Demo Tenant Alert */}
      {settings.is_demo && (
        <Alert variant="warning" className="border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 dark:text-amber-200">Demo Account Restrictions</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            Voice AI settings and BYOK integrations cannot be modified for demo accounts.
          </AlertDescription>
        </Alert>
      )}

      {/* Unified agent tester — talk or type, one live session (always visible) */}
      <VoiceAgentTester />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[740px]">
          <TabsTrigger value="persona" className="gap-2" data-testid="tab-persona">
            <UserCircle className="h-4 w-4" />
            Voice Persona
          </TabsTrigger>
          <TabsTrigger value="market" className="gap-2" data-testid="tab-market">
            <Globe className="h-4 w-4" />
            Market & Language
          </TabsTrigger>
          <TabsTrigger value="quality" className="gap-2" data-testid="tab-quality">
            <Zap className="h-4 w-4" />
            Voice Quality
          </TabsTrigger>
          <TabsTrigger value="transfer" className="gap-2" data-testid="tab-transfer">
            <PhoneForwarded className="h-4 w-4" />
            Human Transfer
          </TabsTrigger>
        </TabsList>

        {/* Voice Persona Tab */}
        <TabsContent value="persona" className="space-y-6 mt-6">
          {loadingPersona ? (
            <Card><CardContent className="py-8"><Skeleton className="h-40 w-full" /></CardContent></Card>
          ) : (
            <>
              {/* Industry Template Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    Voice Agent Persona
                  </CardTitle>
                  <CardDescription>
                    Customize how your AI voice agent sounds and behaves during calls. 
                    {persona?.has_industry_template 
                      ? ` Base template loaded for ${persona.industry_label}.`
                      : ' Set your industry in Profile to get an industry-specific template.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Industry info banner */}
                  {persona?.has_industry_template && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Industry: {persona.industry_label}</AlertTitle>
                      <AlertDescription>
                        Your voice agent uses a base template with industry-specific knowledge, tone, and objection handling.
                        Your customizations below are layered on top.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="agent-name">Agent Name</Label>
                      <Input
                        id="agent-name"
                        placeholder={persona?.agent_name || 'Alex'}
                        value={personaOverrides.agent_name}
                        onChange={(e) => setPersonaOverrides(prev => ({ ...prev, agent_name: e.target.value }))}
                        data-testid="persona-agent-name"
                      />
                      <p className="text-xs text-muted-foreground">
                        The name your AI agent introduces itself as. Default: {persona?.agent_name || 'Alex'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-tone">Tone & Style</Label>
                      <Input
                        id="agent-tone"
                        placeholder={persona?.tone || 'professional and friendly'}
                        value={personaOverrides.tone}
                        onChange={(e) => setPersonaOverrides(prev => ({ ...prev, tone: e.target.value }))}
                        data-testid="persona-tone"
                      />
                      <p className="text-xs text-muted-foreground">
                        e.g. "warm and consultative", "energetic and customer-centric"
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="custom-instructions">Custom Instructions</Label>
                    <textarea
                      id="custom-instructions"
                      rows={4}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      placeholder="Add specific instructions for your voice agent... e.g., 'Always mention our free trial offer' or 'Never discuss competitor pricing'"
                      value={personaOverrides.custom_instructions}
                      onChange={(e) => setPersonaOverrides(prev => ({ ...prev, custom_instructions: e.target.value }))}
                      data-testid="persona-custom-instructions"
                    />
                    <p className="text-xs text-muted-foreground">
                      These instructions are added to the AI's prompt during calls
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="custom-objections">Custom Objection Handling</Label>
                    <textarea
                      id="custom-objections"
                      rows={4}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      placeholder={'e.g., If they say "too expensive", respond with: "Our ROI typically pays for itself in 3 months..."'}
                      value={personaOverrides.custom_objections}
                      onChange={(e) => setPersonaOverrides(prev => ({ ...prev, custom_objections: e.target.value }))}
                      data-testid="persona-custom-objections"
                    />
                    <p className="text-xs text-muted-foreground">
                      Custom responses for common objections your leads raise
                    </p>
                  </div>
                </CardContent>
                <CardFooter className="border-t pt-4 flex justify-between">
                  <Button variant="outline" onClick={loadPromptPreview} data-testid="preview-persona-btn">
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Prompt
                  </Button>
                  <Button onClick={savePersona} disabled={savingPersona} data-testid="save-persona-btn">
                    {savingPersona ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {savingPersona ? 'Saving...' : 'Save Persona'}
                  </Button>
                </CardFooter>
              </Card>

              {/* Prompt Preview Dialog */}
              {showPromptPreview && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">System Prompt Preview</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowPromptPreview(false)}>Close</Button>
                    </div>
                    <CardDescription>This is the actual prompt sent to the AI during voice calls</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted rounded-lg p-4 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap font-mono" data-testid="prompt-preview-content">
                      {promptPreview}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Market & Language Tab */}
        <TabsContent value="market" className="space-y-6 mt-6">
          <MarketLanguageTab />
        </TabsContent>

        {/* Voice Quality Tab (Phase 3D + 3C) */}
        <TabsContent value="quality" className="space-y-6 mt-6">
          <VoiceQualityTab />
        </TabsContent>

        {/* Human Transfer Tab */}
        <TabsContent value="transfer" className="space-y-6 mt-6">
          {/* Human Transfer Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PhoneForwarded className="h-5 w-5" />
                Human Transfer Settings
              </CardTitle>
              <CardDescription>
                Configure how Voice AI hands off calls to human agents when needed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="human-transfer-toggle" className="font-medium">
                      Enable Human Transfer
                    </Label>
                    {settings.human_transfer_enabled ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        Disabled
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Allow Voice AI to transfer calls to human agents when confidence is low, 
                    caller requests it, or knowledge base lacks required information.
                  </p>
                </div>
                <Switch
                  id="human-transfer-toggle"
                  checked={settings.human_transfer_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({
                    ...prev,
                    human_transfer_enabled: checked
                  }))}
                  disabled={settings.is_demo}
                  data-testid="human-transfer-toggle"
                />
              </div>

              <Separator />

              {/* Fallback Agent Number */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="fallback-number" className="font-medium">
                    Fallback Agent Number
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  The phone number or queue to transfer calls to when human assistance is needed.
                </p>
                <div className="flex gap-2">
                  <Input
                    id="fallback-number"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={settings.fallback_agent_number}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      fallback_agent_number: e.target.value
                    }))}
                    disabled={settings.is_demo}
                    className="max-w-xs"
                    data-testid="fallback-number-input"
                  />
                </div>
                {settings.human_transfer_enabled && !settings.fallback_agent_number && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    A fallback number is required when Human Transfer is enabled
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t pt-4">
              <Button 
                onClick={handleSave} 
                disabled={saving || !hasChanges || settings.is_demo}
                data-testid="save-transfer-settings-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Transfer Triggers Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Transfer Triggers
              </CardTitle>
              <CardDescription>
                Voice AI will request a human handoff under these conditions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Low Confidence</p>
                    <p className="text-xs text-muted-foreground">
                      AI confidence score falls below configured threshold
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900">
                    <UserCircle className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Caller Request</p>
                    <p className="text-xs text-muted-foreground">
                      Caller explicitly asks to speak with a human
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900">
                    <Bot className="h-4 w-4 text-orange-600 dark:text-orange-300" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">KB Not Found</p>
                    <p className="text-xs text-muted-foreground">
                      Knowledge base doesn't contain required information
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                    <PhoneForwarded className="h-4 w-4 text-green-600 dark:text-green-300" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Manual Transfer</p>
                    <p className="text-xs text-muted-foreground">
                      User clicks transfer button from Call Details page
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
